import Intercom from "@intercom/messenger-js-sdk";
import { useCallback } from "react";

import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { EnvVar } from "@/env";
import { AnalyticsEvent } from "@/Utils/analytics/events";
import useAnalyticsContext from "@/Utils/analytics/useAnalyticsContext";
import { Layer, layerToDisplayName } from "@/Utils/hierarchy";

interface ActionPlanEventParams {
  actionPlanId: string;
  actionPlanName: string;
  actionPlanLength: number;
  actionPlanSpend: number;
  actionPlanStatus: string;
  actionPlanRevenueGoal: number;
  actionPlanRevenueForecast: number;
}

export const useAnalytics = () => {
  const { analytics } = useAnalyticsContext();
  const { userData: user, currentOrganization: org, tree } = useInfo();

  const availableLayers = Object.keys(tree?.getAllNodePerLayer() || {});

  // store context for analytics tracking
  const context = {
    groupId: "",
    user_email: user?.email,
    organisation_id: org?.id,
    organisation_name: org?.displayName,
    insights_tenant_id: org?.insightsTenantId,
  };

  const moduleViewed = useCallback(
    (type: string, layer: Layer, durationMS: number) => {
      analytics.track(
        AnalyticsEvent.ModuleViewed,
        {
          type,
          level: layerToDisplayName(availableLayers || [], layer),
          duration: Math.floor((durationMS / 1000) % 60),
        },
        {
          context: context,
        }
      );
    },
    [analytics, availableLayers, context]
  );

  const moduleOpened = useCallback(
    (type: string, layer: Layer, enabled: boolean = true) => {
      analytics.track(
        AnalyticsEvent.ModuleOpened,
        {
          type,
          level: layerToDisplayName(availableLayers || [], layer),
          enabled,
        },
        {
          context: context,
        }
      );
    },
    [analytics, availableLayers, context]
  );

  const dashboardViewed = useCallback(
    (type: string, layer: Layer, nodeName: string[] = []) => {
      analytics.track(
        AnalyticsEvent.DashboardViewed,
        {
          type,
          level: layerToDisplayName(availableLayers || [], layer),
          level_title: nodeName.join(" - "),
        },
        {
          context: context,
        }
      );
    },
    [analytics, availableLayers, context]
  );

  const identifyUser = useCallback(
    (
      userId: string,
      firstName: string,
      lastName: string,
      tenantId: string,
      intercomUserJwt?: string
    ) => {
      // Send user identity to Segment for analytics and other integrations
      analytics.identify(userId, {
        user_id: userId,
        first_name: firstName,
        last_name: lastName,
        organisation_id: tenantId,
        organisation_name: tenantId,
      });

      // Authenticate with Intercom using official SDK
      if (intercomUserJwt && EnvVar.intercomAppId) {
        try {
          Intercom({
            app_id: EnvVar.intercomAppId,
            intercom_user_jwt: intercomUserJwt,
          });
        } catch (error) {
          console.error("Failed to initialize Intercom", error);
        }
      } else if (!intercomUserJwt) {
        console.warn("Intercom not initialized: missing JWT token");
      } else if (!EnvVar.intercomAppId) {
        console.warn("Intercom not initialized: missing INTERCOM_APP_ID environment variable");
      }
    },
    [analytics]
  );

  const identifyGroup = useCallback(
    (tenantId: string) => {
      analytics.group(tenantId, {});
      context.groupId = tenantId;
    },
    [analytics, context]
  );

  const scenarioSubmitted = useCallback(
    (params: {
      scenarioName: string;
      scenarioId: string;
      scenarioType: string;
      budgetRatio?: number;
      scenarioLengthMonths?: number;
      mediaSpendInputs?: number;
      mediaSpendConstraints?: number;
      isQuickScenario?: boolean;
      version?: number;
    }) => {
      analytics.track(
        AnalyticsEvent.ScenarioSubmitted,
        {
          scenario_name: params.scenarioName,
          scenario_id: params.scenarioId,
          scenario_type: params.scenarioType,
          budget_ratio: params.budgetRatio,
          scenario_length_months: params.scenarioLengthMonths,
          media_spend_inputs: params.mediaSpendInputs,
          media_spend_constraints: params.mediaSpendConstraints,
          is_quick_scenario: Boolean(params.isQuickScenario),
          version: params.version,
        },
        {
          context: context,
        }
      );
    },
    [analytics, context]
  );

  const scenarioReportViewed = useCallback(
    (scenarioName: string, scenarioId: string, scenarioType: string, version?: number) => {
      analytics.track(
        AnalyticsEvent.ScenarioReportViewed,
        {
          scenario_name: scenarioName,
          scenario_id: scenarioId,
          scenario_type: scenarioType,
          version: version,
        },
        {
          context: context,
        }
      );
    },
    [analytics, context]
  );

  const scenarioCreated = useCallback(
    (params: {
      scenarioType: string;
      scenarioId: string;
      scenarioName: string;
      isCloned?: boolean;
      isQuickScenario?: boolean;
      version?: number;
    }) => {
      analytics.track(
        AnalyticsEvent.ScenarioCreated,
        {
          scenario_type: params.scenarioType,
          scenario_id: params.scenarioId,
          scenario_name: params.scenarioName,
          is_cloned: Boolean(params.isCloned),
          is_quick_scenario: Boolean(params.isQuickScenario),
          version: params.version,
        },
        {
          context: context,
        }
      );
    },
    [analytics, context]
  );

  const scenarioUpdated = useCallback(
    (params: {
      scenarioType: string;
      scenarioId: string;
      scenarioName: string;
      version?: number;
    }) => {
      analytics.track(
        AnalyticsEvent.ScenarioUpdated,
        {
          scenario_type: params.scenarioType,
          scenario_id: params.scenarioId,
          scenario_name: params.scenarioName,
          version: params.version,
        },
        {
          context: context,
        }
      );
    },
    [analytics, context]
  );

  const scenarioFormError = useCallback(
    (params: {
      scenarioId: string;
      scenarioName: string;
      scenarioType: string;
      scenarioError: string;
      version?: number;
    }) => {
      analytics.track(
        AnalyticsEvent.ScenarioFormError,
        {
          scenario_id: params.scenarioId,
          scenario_name: params.scenarioName,
          scenario_type: params.scenarioType,
          scenario_input_errors: params.scenarioError,
          version: params.version,
        },
        {
          context: context,
        }
      );
    },
    [analytics, context]
  );

  const scenarioStatusUpdated = useCallback(
    (
      scenarioType: string,
      scenarioId: string,
      scenarioName: string,
      previousStatus: string,
      currentStatus: string,
      version?: number
    ) => {
      analytics.track(
        AnalyticsEvent.ScenarioStatusUpdated,
        {
          scenario_type: scenarioType,
          scenario_id: scenarioId,
          scenario_name: scenarioName,
          previous_status: previousStatus,
          current_status: currentStatus,
          version: version,
        },
        {
          context: context,
        }
      );
    },
    [analytics, context]
  );

  const scenarioCommented = useCallback(
    (scenarioType: string, scenarioId: string, scenarioName: string, version?: number) => {
      analytics.track(
        AnalyticsEvent.ScenarioCommented,
        {
          scenario_type: scenarioType,
          scenario_id: scenarioId,
          scenario_name: scenarioName,
          version: version,
        },
        {
          context: context,
        }
      );
    },
    [analytics, context]
  );

  const scenarioReportViewedSection = useCallback(
    (
      scenarioType: string,
      scenarioId: string,
      scenarioName: string,
      section: string,
      durationMS: number,
      version?: number
    ) => {
      analytics.track(
        AnalyticsEvent.ScenarioReportViewedSection,
        {
          scenario_type: scenarioType,
          scenario_id: scenarioId,
          scenario_name: scenarioName,
          section: section,
          duration: Math.floor(durationMS / 1000), // Convert to seconds (removed incorrect % 60)
          version: version,
        },
        {
          context: context,
        }
      );
    },
    [analytics, context]
  );

  const scenarioSpendInputsPageViewed = useCallback(
    (
      scenarioType: string,
      scenarioId: string,
      scenarioName: string,
      durationMS: number,
      version?: number
    ) => {
      analytics.track(
        AnalyticsEvent.ScenarioSpendInputsPage,
        {
          scenario_type: scenarioType,
          scenario_id: scenarioId,
          scenario_name: scenarioName,
          duration: Math.floor(durationMS / 1000), // Convert to seconds (removed incorrect % 60)
          version: version,
        },
        {
          context: context,
        }
      );
    },
    [analytics, context]
  );

  const scenarioBreakdownTableDownloaded = useCallback(
    (scenarioType: string, scenarioId: string, scenarioName: string, version?: number) => {
      analytics.track(
        AnalyticsEvent.ScenarioBreakdownTableDownloaded,
        {
          scenario_type: scenarioType,
          scenario_id: scenarioId,
          scenario_name: scenarioName,
          version: version,
        },
        {
          context: context,
        }
      );
    },
    [analytics, context]
  );

  const actionPlanCreated = useCallback(
    (params: ActionPlanEventParams) => {
      analytics.track(
        AnalyticsEvent.ActionPlanCreated,
        {
          action_plan_id: params.actionPlanId,
          action_plan_name: params.actionPlanName,
          action_plan_length: params.actionPlanLength,
          action_plan_spend: params.actionPlanSpend,
          action_plan_status: params.actionPlanStatus,
          action_plan_revenue_goal: params.actionPlanRevenueGoal,
          action_plan_revenue_forecast: params.actionPlanRevenueForecast,
        },
        {
          context: context,
        }
      );
    },
    [analytics, context]
  );

  const spendingPlanDownloaded = useCallback(
    (params: ActionPlanEventParams) => {
      analytics.track(
        AnalyticsEvent.SpendingPlanDownloaded,
        {
          action_plan_id: params.actionPlanId,
          action_plan_name: params.actionPlanName,
          action_plan_length: params.actionPlanLength,
          action_plan_spend: params.actionPlanSpend,
          action_plan_status: params.actionPlanStatus,
          action_plan_revenue_goal: params.actionPlanRevenueGoal,
          action_plan_revenue_forecast: params.actionPlanRevenueForecast,
        },
        {
          context: context,
        }
      );
    },
    [analytics, context]
  );

  const scenarioRecommendationViewed = useCallback(
    (params: ActionPlanEventParams) => {
      analytics.track(
        AnalyticsEvent.ScenarioRecommendationViewed,
        {
          action_plan_id: params.actionPlanId,
          action_plan_name: params.actionPlanName,
          action_plan_length: params.actionPlanLength,
          action_plan_spend: params.actionPlanSpend,
          action_plan_status: params.actionPlanStatus,
          action_plan_revenue_goal: params.actionPlanRevenueGoal,
          action_plan_revenue_forecast: params.actionPlanRevenueForecast,
        },
        {
          context: context,
        }
      );
    },
    [analytics, context]
  );

  const scenarioRecommendationApprovedForActionPlan = useCallback(
    (params: ActionPlanEventParams) => {
      analytics.track(
        AnalyticsEvent.ScenarioRecommendationApprovedForActionPlan,
        {
          action_plan_id: params.actionPlanId,
          action_plan_name: params.actionPlanName,
          action_plan_length: params.actionPlanLength,
          action_plan_spend: params.actionPlanSpend,
          action_plan_status: params.actionPlanStatus,
          action_plan_revenue_goal: params.actionPlanRevenueGoal,
          action_plan_revenue_forecast: params.actionPlanRevenueForecast,
        },
        {
          context: context,
        }
      );
    },
    [analytics, context]
  );

  const scenarioApprovedForActionPlan = useCallback(
    (params: ActionPlanEventParams) => {
      analytics.track(
        AnalyticsEvent.ScenarioApprovedForActionPlan,
        {
          action_plan_id: params.actionPlanId,
          action_plan_name: params.actionPlanName,
          action_plan_length: params.actionPlanLength,
          action_plan_spend: params.actionPlanSpend,
          action_plan_status: params.actionPlanStatus,
          action_plan_revenue_goal: params.actionPlanRevenueGoal,
          action_plan_revenue_forecast: params.actionPlanRevenueForecast,
        },
        {
          context: context,
        }
      );
    },
    [analytics, context]
  );

  const latestDataAvailable = useCallback(
    (maxDate: string) => {
      analytics.track(
        AnalyticsEvent.LatestDataAvailable,
        {
          max_date: maxDate,
        },
        {
          context: context,
        }
      );
    },
    [analytics, context]
  );

  const buttonClicked = useCallback(
    (params: {
      buttonName: string;
      buttonLocation: string;
      buttonType?: string;
      additionalProperties?: Record<string, any>;
    }) => {
      analytics.track(
        AnalyticsEvent.ButtonClicked,
        {
          button_name: params.buttonName,
          button_location: params.buttonLocation,
          button_type: params.buttonType || "cta",
          ...params.additionalProperties,
        },
        {
          context: context,
        }
      );
    },
    [analytics, context]
  );

  const unitsSelected = useCallback(
    (params: { moduleName: string }) => {
      analytics.track(
        AnalyticsEvent.UnitsSelected,
        {
          module_name: params.moduleName,
        },
        {
          context,
        }
      );
    },
    [analytics, context]
  );

  const splitByBusinessUnit = useCallback(
    (params: { moduleName: string }) => {
      analytics.track(
        AnalyticsEvent.SplitByBusinessUnit,
        {
          module_name: params.moduleName,
        },
        {
          context: context,
        }
      );
    },
    [analytics, context]
  );

  const insightsTableDownloaded = useCallback(
    (params: { moduleName: string }) => {
      analytics.track(
        AnalyticsEvent.InsightsTableDownloaded,
        {
          module_name: params.moduleName,
        },
        {
          context: context,
        }
      );
    },
    [analytics, context]
  );

  return {
    moduleViewed,
    moduleOpened,
    dashboardViewed,
    identifyUser,
    identifyGroup,
    scenarioSubmitted,
    scenarioReportViewed,
    scenarioCreated,
    scenarioUpdated,
    scenarioFormError,
    scenarioStatusUpdated,
    scenarioCommented,
    scenarioReportViewedSection,
    scenarioSpendInputsPageViewed,
    scenarioBreakdownTableDownloaded,
    actionPlanCreated,
    spendingPlanDownloaded,
    scenarioRecommendationViewed,
    scenarioRecommendationApprovedForActionPlan,
    scenarioApprovedForActionPlan,
    latestDataAvailable,
    buttonClicked,
    unitsSelected,
    splitByBusinessUnit,
    insightsTableDownloaded,
  };
};
